import * as React from 'react';
import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { Card, CardHeader } from './ui/card';
import { cn } from './lib/utils';

const GroupContext = React.createContext(null);

/**
 * Coordinates a set of `CollapsibleCard`s: the sections marked `defaultOpen`
 * start open (falling back to the first rendered one when none are marked),
 * and every section can be opened or closed at once.
 */
export function CollapsibleCardGroup({ className, children }) {
  const [order, setOrder] = React.useState([]);
  const [preferred, setPreferred] = React.useState([]);
  const [openById, setOpenById] = React.useState({});

  const register = React.useCallback((id, wantsOpen) => {
    setOrder((prev) => (prev.includes(id) ? prev : [...prev, id]));
    if (wantsOpen) {
      setPreferred((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
    return () => {
      setOrder((prev) => prev.filter((entry) => entry !== id));
      setPreferred((prev) => prev.filter((entry) => entry !== id));
      setOpenById((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };
  }, []);

  const value = React.useMemo(() => {
    // A section the report marked as default wins; otherwise open the first one
    // that rendered, so the report is never fully collapsed on arrival.
    const isOpenByDefault = (id) =>
      preferred.length > 0 ? preferred.includes(id) : order[0] === id;
    const isOpen = (id) => (id in openById ? openById[id] : isOpenByDefault(id));

    return {
      register,
      isOpen,
      toggle: (id) =>
        setOpenById((prev) => ({
          ...prev,
          [id]: !(id in prev ? prev[id] : isOpenByDefault(id)),
        })),
      setAll: (open) =>
        setOpenById(
          order.reduce((acc, id) => {
            acc[id] = open;
            return acc;
          }, {})
        ),
      count: order.length,
      openCount: order.filter(isOpen).length,
    };
  }, [order, preferred, openById, register]);

  return (
    <GroupContext.Provider value={value}>
      <div className={className}>{children}</div>
    </GroupContext.Provider>
  );
}

export function useCollapsibleCardGroup() {
  return React.useContext(GroupContext);
}

/** Expand-all / collapse-all toggle for the surrounding group. */
export function CollapsibleCardGroupControls({ className }) {
  const group = useCollapsibleCardGroup();

  if (!group || group.count < 2) {
    return null;
  }

  const allOpen = group.openCount === group.count;

  return (
    <div className={cn('no-print flex justify-end', className)}>
      <button
        type="button"
        onClick={() => group.setAll(!allOpen)}
        className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        {allOpen ? (
          <ChevronsDownUp className="h-4 w-4" />
        ) : (
          <ChevronsUpDown className="h-4 w-4" />
        )}
        {allOpen ? 'Collapse all' : 'Expand all'}
      </button>
    </div>
  );
}

const isInteractive = (target) =>
  Boolean(
    target?.closest?.('button, a, input, select, textarea, [role="button"]')
  );

/**
 * A report section card whose body collapses when its header is clicked.
 * Takes the same children as `Card`; the `CardHeader` child gets the toggle.
 * Inside a `CollapsibleCardGroup` the group decides which sections are open.
 */
export function CollapsibleCard({
  className,
  children,
  defaultOpen,
  ...props
}) {
  const items = React.Children.toArray(children);
  const header = items.find(
    (child) => React.isValidElement(child) && child.type === CardHeader
  );

  const group = useCollapsibleCardGroup();
  const id = React.useId();
  // On its own a card starts open; in a group `defaultOpen` marks the one
  // section that should be open when the report first renders.
  const [standaloneOpen, setStandaloneOpen] = React.useState(
    defaultOpen !== false
  );

  const register = group?.register;
  const hasHeader = Boolean(header);
  const wantsOpen = defaultOpen === true;
  React.useLayoutEffect(() => {
    // Only sections that can actually collapse join the group.
    if (!register || !hasHeader) return undefined;
    return register(id, wantsOpen);
  }, [register, id, hasHeader, wantsOpen]);

  if (!header) {
    return (
      <Card className={className} {...props}>
        {children}
      </Card>
    );
  }

  const body = items.filter((child) => child !== header);
  const open = group ? group.isOpen(id) : standaloneOpen;
  const toggle = () =>
    group ? group.toggle(id) : setStandaloneOpen((value) => !value);

  return (
    <Card className={className} {...props}>
      {React.cloneElement(header, {
        className: cn(header.props.className, 'cursor-pointer select-none'),
        onClick: (event) => {
          // Export buttons and links inside the header keep their own behaviour.
          if (isInteractive(event.target)) {
            return;
          }
          header.props.onClick?.(event);
          toggle();
        },
        children: (
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-1.5">
              {header.props.children}
            </div>
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              aria-label={open ? 'Collapse section' : 'Expand section'}
              className="no-print shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ChevronDown
                className={cn(
                  'h-5 w-5 transition-transform duration-200',
                  !open && '-rotate-90'
                )}
              />
            </button>
          </div>
        ),
      })}
      {/* Stays mounted while collapsed so print output is always complete. */}
      <div
        className="report-section-content"
        style={open ? undefined : { display: 'none' }}
      >
        {body}
      </div>
    </Card>
  );
}

export default CollapsibleCard;
